export const COMPACT_LOGO_RATIO_RANGE = { min: 0.8, max: 1.25 };
export const WIDE_LOGO_RATIO_RANGE = { min: 2.5, max: 5.5 };

type ThemeAssetType = "logo" | "wideLogo" | "favicon";
type Dimensions = { width: number; height: number };

export type ThemeAssetValidationResult =
  | { valid: true }
  | {
      valid: false;
      reason:
        | "compactLogoAspectRatio"
        | "wideLogoAspectRatio"
        | "invalidImageDimensions";
    };

const isPositiveNumber = (value: number) =>
  Number.isFinite(value) && value > 0;

export const parseSvgViewBoxDimensions = (
  svgText: string,
): Dimensions | null => {
  const viewBox = svgText.match(
    /<svg\b[^>]*\bviewBox\s*=\s*["']([^"']+)["']/i,
  )?.[1];
  const values = viewBox
    ?.trim()
    .split(/[\s,]+/)
    .map(Number);

  if (!values || values.length !== 4) return null;

  const width = values[2];
  const height = values[3];

  return isPositiveNumber(width) && isPositiveNumber(height)
    ? { width, height }
    : null;
};

export const validateThemeAssetDimensions = (
  type: ThemeAssetType,
  dimensions: Dimensions | null,
): ThemeAssetValidationResult => {
  if (type === "favicon") return { valid: true };
  if (!dimensions) {
    return { valid: false, reason: "invalidImageDimensions" };
  }

  const ratio = dimensions.width / dimensions.height;
  const range =
    type === "wideLogo" ? WIDE_LOGO_RATIO_RANGE : COMPACT_LOGO_RATIO_RANGE;

  if (ratio < range.min || ratio > range.max) {
    return {
      valid: false,
      reason:
        type === "wideLogo"
          ? "wideLogoAspectRatio"
          : "compactLogoAspectRatio",
    };
  }

  return { valid: true };
};

const loadRasterDimensions = (file: File): Promise<Dimensions> =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    const cleanup = () => URL.revokeObjectURL(objectUrl);

    image.onload = () => {
      const dimensions = {
        width: image.naturalWidth,
        height: image.naturalHeight,
      };
      cleanup();
      resolve(dimensions);
    };
    image.onerror = () => {
      cleanup();
      reject(new Error("Unable to read image dimensions"));
    };
    image.src = objectUrl;
  });

export const validateThemeAssetFile = async (
  file: File,
  type: ThemeAssetType,
): Promise<ThemeAssetValidationResult> => {
  if (type === "favicon") return { valid: true };

  try {
    const dimensions =
      file.type === "image/svg+xml"
        ? parseSvgViewBoxDimensions(await file.text())
        : await loadRasterDimensions(file);

    return validateThemeAssetDimensions(type, dimensions);
  } catch {
    return { valid: false, reason: "invalidImageDimensions" };
  }
};
